import { ReactNode, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import { Dialog } from "../../../../common/core-ui/Dialog.tsx";
import MiscellaneousSearch from "../../../../common/icons/MiscellaneousSearch.tsx";
import EditFolder from "../../../../common/icons/EditFolder.tsx";
import useFacultyWelcomeModal, {
  FacultyWelcomeModalCohort,
} from "./useFacultyWelcomeModal.ts";
import styles from "./FacultyWelcomeModal.module.scss";
import MiscellaneousRecommendation from "../../../../common/icons/MiscellaneousRecommendation.tsx";
import { useBrandLoaderData } from "../../../root/rootLoader.ts";
import MiscellaneousCheckmarkCircle from "../../../../common/icons/MiscellaneousCheckmarkCircle.tsx";
import MiscellaneousExclamationCircle from "../../../../common/icons/MiscellaneousExclamationCircle.tsx";

function FeatureRow({
  icon,
  title,
  description,
}: {
  icon: ReactNode;
  title: ReactNode;
  description: ReactNode;
}) {
  return (
    <li className={styles.featureRow}>
      <div className={styles.featureIcon}>{icon}</div>
      <div>
        <div className={styles.featureTitle}>{title}</div>
        <div>{description}</div>
      </div>
    </li>
  );
}

export function FacultyWelcomeModalBody({
  cohort,
}: {
  cohort: FacultyWelcomeModalCohort;
}) {
  const isVerified = cohort === "verified";

  return (
    <>
      <div
        className={clsx(
          styles.statusRow,
          "vst-tag",
          isVerified ? "vst-tag-variant-green" : "vst-tag-variant-red"
        )}
      >
        {isVerified ? (
          <MiscellaneousCheckmarkCircle />
        ) : (
          <MiscellaneousExclamationCircle />
        )}
        {isVerified ? (
          <FormattedMessage defaultMessage="You're a verified instructor." />
        ) : (
          <FormattedMessage defaultMessage="Your instructor status is unverified." />
        )}
      </div>
      <ul className={styles.featureList}>
        <FeatureRow
          icon={<MiscellaneousSearch />}
          title={
            <FormattedMessage defaultMessage="Instantly Sample eBooks for Free" />
          }
          description={
            <FormattedMessage defaultMessage="Browse free eBook samples, evaluate them for course fit." />
          }
        />
        <FeatureRow
          icon={<EditFolder />}
          title={<FormattedMessage defaultMessage="Course Fit Insights" />}
          description={
            <FormattedMessage defaultMessage="Instant access to course fit analytics powered by Open Syllabus." />
          }
        />
        <FeatureRow
          icon={<MiscellaneousRecommendation />}
          title={
            <FormattedMessage defaultMessage="Publisher Recommendations" />
          }
          description={
            <FormattedMessage defaultMessage="Publisher pushed recommendations available." />
          }
        />
      </ul>
      {!isVerified && (
        <div className={styles.verificationCallout}>
          <div className={styles.verificationCalloutTitle}>
            <FormattedMessage defaultMessage="How do I get verified to unlock instructor features?" />
          </div>
          <ol className={styles.verificationSteps}>
            <li>
              <FormattedMessage defaultMessage="First, search for a free eBook sample of a title you're curious about." />
            </li>
            <li>
              <FormattedMessage defaultMessage="Then provide your course information and instructor email." />
            </li>
            <li>
              <FormattedMessage defaultMessage="Finally, get verified by the publisher." />
            </li>
          </ol>
        </div>
      )}
    </>
  );
}

interface FacultyWelcomeModalDialogProps {
  cohort: FacultyWelcomeModalCohort;
  brandName: string;
  onRequestClose: () => void;
  onContinue: () => void;
  onBrowseSamples: () => void;
}

export function FacultyWelcomeModalDialog({
  cohort,
  brandName,
  onRequestClose,
  onContinue,
  onBrowseSamples,
}: FacultyWelcomeModalDialogProps) {
  const intl = useIntl();
  const isVerified = cohort === "verified";

  return (
    <Dialog
      darkHeader
      onRequestClose={onRequestClose}
      customWidth="70rem"
      title={
        isVerified ? (
          <FormattedMessage defaultMessage="We've Added New Instructor Features!" />
        ) : (
          <FormattedMessage defaultMessage="Unlock New Instructor Features!" />
        )
      }
      secondaryButton={{
        label: intl.formatMessage(
          { defaultMessage: "Continue to {brandName}" },
          { brandName }
        ),
        onClick: onContinue,
      }}
      primaryButton={{
        label: intl.formatMessage({
          defaultMessage: "Browse Free eBook Samples",
        }),
        onClick: onBrowseSamples,
      }}
    >
      <FacultyWelcomeModalBody cohort={cohort} />
    </Dialog>
  );
}

export default function FacultyWelcomeModal() {
  const navigate = useNavigate();
  const brand = useBrandLoaderData();
  const { shouldShow, cohort, dismiss, logCtaClick } = useFacultyWelcomeModal();
  const [isClosed, setIsClosed] = useState(false);

  if (!shouldShow || isClosed) {
    return null;
  }

  return (
    <FacultyWelcomeModalDialog
      cohort={cohort}
      brandName={brand.name}
      onRequestClose={() => {
        dismiss("close");
        setIsClosed(true);
      }}
      onContinue={() => {
        logCtaClick("secondary_cta");
        dismiss("secondary_cta");
        setIsClosed(true);
      }}
      onBrowseSamples={() => {
        logCtaClick("primary_cta");
        dismiss("primary_cta");
        void navigate("/search/sample");
      }}
    />
  );
}
